<script setup>
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesDeliveryCommission from './SalesDeliveryCommission.vue';
import SalesDeliveryDates from './SalesDeliveryDates.vue';
import SalesDeliveryEmails from './SalesDeliveryEmails.vue';
import SalesDeliveryReceiptType from './SalesDeliveryReceiptType.vue';
import SalesDeliveryTopics from './SalesDeliveryTopics.vue';
import SalesDeliveryWitnesses from './SalesDeliveryWitnesses.vue';
import SalesReceiptAnalysisCard from './SalesReceiptAnalysisCard.vue';

defineProps({
  delivery: { type: Object, required: true },
  receiptAnalysis: { type: Object, required: true },
  transaction: { type: Object, default: null },
  today: { type: Date, default: undefined },
});

const emit = defineEmits([
  'confirm-conflict',
  'correct-conflict',
  'edit-conflict',
  'update',
  'update-receipt',
  'update-witness',
]);
const { t } = useI18n();
</script>

<template>
  <main
    class="mx-auto flex w-full max-w-[1500px] flex-col gap-5 px-4 pb-8 sm:px-6 lg:px-10"
  >
    <div
      v-if="delivery.loading.value"
      class="rounded-lais border border-lais-border-soft bg-white p-8 text-center text-sm text-lais-text-muted"
    >
      {{ t('SALES.COMMON.LOADING') }}
    </div>
    <template v-else-if="transaction">
      <LaisCard :title="transaction.title" prefix-icon="i-lucide-house">
        <p class="text-sm text-lais-text-muted">
          {{
            transaction.address?.endereco ||
            t('SALES.COMMON.ADDRESS_UNAVAILABLE')
          }}
        </p>
        <p
          class="mt-2 text-xs font-bold uppercase tracking-wide text-lais-primary"
        >
          {{ transaction.sales_stage_label }}
        </p>
      </LaisCard>
      <SalesDeliveryReceiptType
        :value="delivery.form.value.recibo_type"
        :error="delivery.errors.value.recibo_type"
        :disabled="!delivery.canEdit.value"
        @update="emit('update-receipt', $event)"
      />
      <SalesReceiptAnalysisCard
        :analysis="receiptAnalysis.analysis.value"
        :can-resolve="receiptAnalysis.canResolve.value"
        :loading="receiptAnalysis.loading.value"
        :resolving-id="receiptAnalysis.resolvingId.value"
        @confirm="emit('confirm-conflict', $event)"
        @correct="emit('correct-conflict', $event)"
        @edit="emit('edit-conflict', $event)"
      />
      <div class="grid gap-5 xl:grid-cols-2">
        <SalesDeliveryDates
          :form="delivery.form.value"
          :errors="delivery.errors.value"
          :disabled="!delivery.canEdit.value"
          :today="today"
          @update="(field, value) => emit('update', field, value)"
        />
        <SalesDeliveryCommission
          :form="delivery.form.value"
          :errors="delivery.errors.value"
          :disabled="!delivery.canEdit.value"
          @update="(field, value) => emit('update', field, value)"
        />
      </div>
      <div
        v-if="delivery.form.value.recibo_type === 'docusign'"
        class="grid gap-5 xl:grid-cols-2"
      >
        <SalesDeliveryEmails
          :participants="delivery.participants.value"
          :copies="delivery.copies.value"
          :checked="delivery.form.value.email_check"
          :error="delivery.errors.value.email_check"
          :disabled="!delivery.canEdit.value"
          :saving-recipients="delivery.recipientSaving.value"
          :save-recipients="delivery.saveRecipients"
          @update="value => emit('update', 'email_check', value)"
        />
        <SalesDeliveryWitnesses
          :witnesses="delivery.form.value.testemunhas"
          :errors="delivery.errors.value"
          :disabled="!delivery.canEdit.value"
          @update="
            (index, field, value) => emit('update-witness', index, field, value)
          "
        />
      </div>
      <SalesDeliveryTopics
        :form="delivery.form.value"
        :errors="delivery.errors.value"
        :responsible-type="delivery.responsibleType.value"
        :banks="delivery.bankOptions.value"
        :participants="delivery.participants.value"
        :disabled="!delivery.canEdit.value"
        @update="(field, value) => emit('update', field, value)"
      />
    </template>
  </main>
</template>
